
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import JournalForm from "@/components/journal/JournalForm";
import { JournalStage } from "@/hooks/useJournalFlow";
import { JournalFormValues } from "@/components/journal/form/JournalFormSchema";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { BookOpen, Scroll } from "lucide-react";
import { DifficultyRank } from "@/utils/difficultyRanks";

// Format date for display
const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface JournalEditorProps {
  journalEntry: {
    updated_at?: string;
    difficulty_rank?: string | null; // Accept string from database
  } | null;
  isNewEntry: boolean;
  formDefaultValues: JournalFormValues;
  currentStage: JournalStage;
  activeDiceValue: number;
  journalPrompts: Record<number, {
    perception: string;
    intention: string;
    nextStep: string;
  }>;
  onContinue: () => void;
  onRollDice: () => void;
  onSave: (values: JournalFormValues, difficultyRank?: DifficultyRank) => void;
  hasDiceRolled?: boolean;
  userHasStartedTyping?: boolean;
  onUserStartedTyping?: () => void;
  onReRoll: () => void;
}

const JournalEditor: React.FC<JournalEditorProps> = ({
  journalEntry,
  isNewEntry,
  formDefaultValues,
  currentStage,
  activeDiceValue,
  journalPrompts,
  onContinue,
  onRollDice,
  onSave,
  hasDiceRolled = false,
  userHasStartedTyping = false,
  onUserStartedTyping,
  onReRoll
}) => {
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);

  console.log("JournalEditor rendering with stage:", currentStage);
  console.log("Active dice value:", activeDiceValue);
  console.log("Form default values:", formDefaultValues);
  console.log("Has dice rolled:", hasDiceRolled);
  console.log("User has started typing:", userHasStartedTyping);

  // Cast string to DifficultyRank for the JournalForm component
  const existingDifficultyRank = journalEntry?.difficulty_rank as DifficultyRank | null;

  return (
    <Card className={cn(
      "flex-1 relative",
      enableRpgMode ? (
        rpgTheme.cardBackground + " border-2 border-amber-600/50 shadow-lg"
      ) : ""
    )}>
      {enableRpgMode && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/10 to-amber-900/20 rounded-lg pointer-events-none" />
      )}
      
      <CardHeader className="relative z-10">
        <CardTitle className={cn(
          "flex items-center gap-2",
          enableRpgMode && rpgTheme.text + " font-bold drop-shadow-sm"
        )}>
          {enableRpgMode ? (
            <>
              <Scroll className="h-5 w-5 text-amber-500" />
              <span>Quest Log</span>
            </>
          ) : (
            <span>Journal Entry</span>
          )}
        </CardTitle>
        <CardDescription className={cn(
          enableRpgMode && rpgTheme.accent
        )}>
          {!isNewEntry && journalEntry
            ? `Last updated on ${formatDate(journalEntry?.updated_at)}`
            : enableRpgMode 
              ? "Chronicle your adventure and overcome obstacles." 
              : "Create a new journal entry."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="relative z-10">
        <JournalForm 
          defaultValues={formDefaultValues}
          currentStage={currentStage}
          diceValue={activeDiceValue}
          twistActive={false}
          onContinue={onContinue}
          onRollDice={onRollDice}
          onSave={onSave}
          journalPrompts={journalPrompts}
          hasDiceRolled={hasDiceRolled}
          userHasStartedTyping={userHasStartedTyping}
          onUserStartedTyping={onUserStartedTyping}
          onReRoll={onReRoll}
          existingDifficultyRank={existingDifficultyRank}
          isNewEntry={isNewEntry}
        />
      </CardContent>
    </Card>
  );
};

export default JournalEditor;
